import React, { useCallback, useContext, useEffect, useState } from 'react';
import { CreationFundContext } from '../CreationFundContext';
import { TextField } from '@material-ui/core';
import { Loading } from 'react-admin';
import ContactsList from './ContactsList';
import InvestorApi from '../../../../core/apis/InvestorApi';
import GroupsApi from '../../../../core/apis/GroupApi';
import useNotifyServerError from '../../../../core/hooks/useNotifyServerError';
import { useProfile } from '../../../../core/hooks/UseProfile';
import { useStyles } from '../../../users/userauthorization/stepper/styles';
import {
  createUserAuthorizations,
  setSearch
} from '../../../users/userauthorization/stepper/utils';
import { useGetAuthorizations } from '../../../investors/copyContactsSteps/ContactsStep';

const AddRelatedContacts = ({
  authorizationsGridToDeleteRef,
  permissionGroupsUploaded,
  setCurrentAuthorizations,
  currentAuthorizations,
  onSubmitEnd,
  hasAssignUserAuthorization,
  contacts,
  ...props
}) => {
  const { fundContext } = useContext(CreationFundContext);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setIsLoading] = useState(true);
  const notifyServerError = useNotifyServerError();
  const { profile: connectedUserProfile } = useProfile();

  const classes = useStyles();

  const fetchContactsAndGroups = useCallback(async () => {
    const fetchContacts = InvestorApi.getRelatedContacts(
      fundContext.investorIds
    );
    const fetchGroups = GroupsApi.getActiveNonAdminGroups();
    return Promise.all([fetchContacts, fetchGroups]);
  }, [fundContext.investorIds]);

  const { data: connectedUserAuthorizations } = useGetAuthorizations(
    connectedUserProfile
  );

  useEffect(() => {
    fetchContactsAndGroups()
      .then(([fetchedContacts, fetchedGroups]) => {
        // In the event that an investor is removed during step 2, it is necessary to remove any permissions associated with the corresponding contact that have been stored.
        if (contacts.current.length > 0) {
          const contactsRemoved = contacts.current.reduce((acc, curr) => {
            if (
              !fetchedContacts.some(contact => contact.userId === curr.userId)
            ) {
              acc = [...acc, curr];
            }
            return acc;
          }, []);
          if (contactsRemoved.length > 0) {
            setCurrentAuthorizations(prev => [
              ...prev.reduce((acc, curr) => {
                if (
                  !contactsRemoved.some(
                    contact => contact.userId === curr.userId
                  )
                ) {
                  acc = [...acc, curr];
                }
                return acc;
              }, [])
            ]);
          }
        }
        contacts.current = fetchedContacts;
        setFilteredContacts(fetchedContacts);
        setGroups(fetchedGroups.pageData);
      })
      .catch(e => notifyServerError(e))
      .finally(() => setIsLoading(false));
  }, [
    contacts,
    fetchContactsAndGroups,
    notifyServerError,
    setCurrentAuthorizations
  ]);

  const handleSearchChange = e => {
    setSearch(setFilteredContacts, contacts, e);
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <TextField
        margin="dense"
        placeholder={`Search...`}
        onChange={handleSearchChange}
      />
      <ContactsList
        contacts={filteredContacts}
        classes={classes}
        groups={groups}
        authorizationsConnectedUser={createUserAuthorizations(
          connectedUserAuthorizations
        )}
        connectedUserProfile={connectedUserProfile}
        authorizationsGridToDeleteRef={authorizationsGridToDeleteRef}
        permissionGroupsUploaded={permissionGroupsUploaded}
        setCurrentAuthorizations={setCurrentAuthorizations}
        currentAuthorizations={currentAuthorizations}
        onSubmitEnd={onSubmitEnd}
        hasAssignUserAuthorization={hasAssignUserAuthorization}
        {...props}
      />
    </>
  );
};

export default AddRelatedContacts;
